const { $posthog } = useNuxtApp()
import type { User, UserRole } from '~/types/user'
import type { FeatureFlag } from '~/utils/featureFlags'

export const useAuthStore = defineStore('auth', () => {
    const client = useSupabaseClient()
    const callCenter = useCallCenter()

    const user = ref<User>()

    function hasRole(...roles: UserRole[]) {
        return user.value?.role && roles.includes(user.value.role)
    }

    function isEnabled(flag: FeatureFlag) {
        return user.value?.features?.[flag] === true
    }

    async function setUser(force: boolean = false): Promise<User | undefined> {
        if (force || !user.value) {
            try {
                user.value = await $fetch('/api/auth/profile')
                return user.value
            } catch (e) {
                console.error(e)
                await reset()
                return undefined
            }
        } else {
            return user.value
        }
    }

    async function logout() {
        if (user.value) {
            try {
                const now = new Date().toISOString()

                await client
                    .from('users')
                    .update({
                        login_at: user.value.login_at || now,
                        logout_at: now,
                        metadata: {
                            ...user.value.metadata,
                            extension: undefined
                        }
                    } as never)
                    .eq('id', user.value.id)

                $posthog().reset()
            } catch {
                await reset()
                return
            }
        }

        await reset()
    }

    async function reset() {
        await client.auth.signOut({ scope: 'global' })
        callCenter.resetState()
        user.value = undefined
    }

    return {
        user,
        hasRole,
        isEnabled,
        setUser,
        logout,
    }
})
